import * as React from 'react';

import * as api from '@gitbook/api-client';
import { DatePrettyCalendary, ListItem, prettyDate } from '@gitbook/react-uikit';
import { Button } from '@gitbook/spine-react';

import { priceFormatter } from './formatter';

/**
 * Shows a preview of the given invoice. This includes:
 * - Credit balance
 * - Invoice lines
 * - Amount due
 * - Remaining balance
 */
export function BillingInvoicePreview(props: {
    billingInterval: api.BillingInterval;
    billingPeriodEnd?: api.OrganizationBilling['endDate'];
    invoice: api.BillingInvoicePreview;
}): React.ReactElement {
    const { billingInterval, billingPeriodEnd, invoice } = props;
    const [showAllLines, setShowAllLines] = React.useState(false);
    const invoiceLines = showAllLines ? invoice.lines : invoice.lines.slice(0, 5);

    return (
        <div>
            {invoice.customerBalance > 0 ? (
                <ListItem
                    layout="wide"
                    title="Account credit"
                    hint="Balance decreases the amount due on this invoice."
                    buttons={[
                        <span key="amount">
                            {priceFormatter.format(-invoice.customerBalance / 100)}
                        </span>,
                    ]}
                />
            ) : null}
            {invoiceLines.map((item, index) => (
                <ListItem
                    key={index}
                    layout="wide"
                    title={item.description}
                    hint={
                        <>
                            From <DatePrettyCalendary value={new Date(item.periodStart)} /> to{' '}
                            <DatePrettyCalendary value={new Date(item.periodEnd)} />
                        </>
                    }
                    buttons={[<span key="amount">{priceFormatter.format(item.amount / 100)}</span>]}
                />
            ))}
            {!showAllLines && invoice.lines.length > 5 ? (
                <div className="mt-4 mb-6 flex justify-center">
                    <Button
                        kind="secondary"
                        label={`Show all ${invoice.lines.length} upcoming invoice lines`}
                        onPress={() => setShowAllLines(true)}
                        size="small"
                    />
                </div>
            ) : null}
            <div className="mt-2 flex flex-col border-t">
                {invoice.discount ? (
                    <ListItem
                        layout="wide"
                        title={invoice.discount.description}
                        buttons={[
                            <strong key="discount">
                                {priceFormatter.format(-invoice.discount.amount / 100)}
                            </strong>,
                        ]}
                    />
                ) : null}
                <ListItem
                    layout="wide"
                    title={`Amount due ${billingInterval ? (billingInterval === api.BillingInterval.Monthly ? (billingPeriodEnd ? `on ${prettyDate(new Date(billingPeriodEnd))}` : '') : 'today') : ''}`}
                    buttons={[
                        <strong key="amount">
                            {priceFormatter.format(invoice.amountDueToday / 100)}
                        </strong>,
                    ]}
                />
                {invoice.remainingCustomerBalance > 0 ? (
                    <ListItem
                        layout="wide"
                        title="Remaining invoice credit balance"
                        hint="Balance will decrease the amount due on the next invoices."
                        buttons={[
                            <span key="amount">
                                {priceFormatter.format(invoice.remainingCustomerBalance / 100)}
                            </span>,
                        ]}
                    />
                ) : null}
            </div>
        </div>
    );
}
